import { useForm } from "react-hook-form";
import { FormValues } from "@/components/booking-form/types";
import { useLanguage } from "./useLanguage";
import { useRef } from "react";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import {
  coerceLanguageForMarket,
  getDefaultCountryCode,
} from "@/config/market";

interface UseBookingFormInitProps {
  email: string;
}

let formInstanceCounter = 0;

export const useBookingFormInit = ({ email }: UseBookingFormInitProps) => {
  // Check if the email is anonymous
  const isAnonymousEmail = email?.includes("anonymous-");
  const { language } = useLanguage();
  const market = useBookingFlowStore((s) => s.market);

  const instanceIdRef = useRef<number | null>(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = ++formInstanceCounter;
    console.log(
      "[useBookingFormInit] NEW FORM INSTANCE created, ID:",
      instanceIdRef.current,
    );
  } else {
    console.log(
      "[useBookingFormInit] Reusing form instance ID:",
      instanceIdRef.current,
    );
  }

  const form = useForm<FormValues>({
    mode: "onSubmit", // Only validate on form submission, not on mount or change
    reValidateMode: "onBlur", // Re-validate on blur, not onChange — prevents jumpy phone input (AHD-4514)
    defaultValues: {
      firstName: "",
      lastName: "",
      email: isAnonymousEmail ? "" : email || "", // Don't pre-fill anonymous emails
      birthday: "",
      notes: "",
      genderAtBirth: "",
      gdprConsent: false,
      tosAndPpConsent: false,
      medicalProcessing: false,
      anonymousDataUsage: false,
      // Germany-only private-treatment consents
      deWunschleistungConsent: false,
      deTreatmentConsent: false,
      deConfidentialityConsent: false,
      // Austria-only consents
      atLabAndDoctorConsent: false,
      atConfidentialityConsent: false,
      atWithdrawalConsent: false,
      phone: "",
      // The UI language is detected from the URL, localStorage or the browser
      // and coerced against the market in the URL — which is not always the
      // market the flow ends up in. A value this market does not offer leaves
      // the Select with no matching item, so it renders its placeholder and
      // reads unanswered while holding a value (AHD-12067). Seed the market
      // default instead of the browser's language in that case.
      preferredLanguage: coerceLanguageForMarket(market, language),
      // Address fields
      street: "",
      streetNumber: "",
      zipCode: "",
      city: "",
      country: getDefaultCountryCode(market), // Market default (CH / DE)
    },
    shouldFocusError: false, // Prevent auto-focus on first error, we handle scrolling manually
  });

  // IMPORTANT: In React, useForm returns a proxy that maintains state across renders
  // The "form" object is stable, but formState changes are tracked via proxy
  console.log(
    "[useBookingFormInit] Form state after useForm call:",
    JSON.stringify({
      submitCount: form.formState.submitCount,
      isSubmitted: form.formState.isSubmitted,
      isSubmitSuccessful: form.formState.isSubmitSuccessful,
      errors: Object.keys(form.formState.errors),
      // Check if this is the same form instance by logging internal _formState
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      internalSubmitCount: (form.control as any)?._formState?.submitCount,
    }),
  );

  return form;
};
